import {Box, Show, VStack} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {FocusEventHandler} from 'react';

import {suffixTestId} from 'shared/web';

import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {LevelFill} from '../LevelInput/LevelFill';
import {LevelInputProps} from '../LevelInput/LevelInputProps';
import {LevelInputRow} from '../LevelInput/LevelInputRow';
import {EMPTY_LABEL, FULL_LABEL} from '../LevelInput/levelLabels';
import {LevelPreview} from '../LevelInput/LevelPreview';
import {useLevelInput} from '../LevelInput/useLevelInput';
import {DEFAULT_LEVEL_MAX, DEFAULT_LEVEL_MIN} from '../LevelInput/utils/resolveLevel';
import {snapLevelToProps} from '../LevelInput/utils/snapLevelToProps';
import {NumberInput} from '../NumberInput/NumberInput';

export type FuelLevelProps = LevelInputProps & {
  /**
   * Adds a precise percentage input in front of the gauge, for readings typed from a dashboard.
   *
   * The input edits the gauge value directly, in the unit the value is stored in, and it is
   * labelled and stepped as whole percent. Only enable it on gauges whose value is a 0–100
   * percentage (the workshop forms); a ratio-based caller (0–1) would show "0 %" to "1 %".
   */
  isValueInputVisible?: boolean;
};

/**
 * Gap from the reviewed design that does not land on the 4px spacing grid
 * (`theme.space.step`). It is drawn as a raw CSS pixel length under the
 * no-raw-pixels skill's fixed-pixel exception — rounding 6px to 4px or 8px
 * would visibly change the gauge.
 */
const ROW_GAP = '6px';

/** Fits "100 %" inside the input's horizontal padding without the suffix wrapping. */
const VALUE_INPUT_WIDTH = 22;
/** The gauge counts in whole percent, so the precise input takes no decimals. */
const VALUE_INPUT_DECIMAL_PLACES = 0;
const VALUE_INPUT_SUFFIX = '%';
const FUEL_LEVEL_DEFAULT_STEP_FRACTION = 0.25;

/** Decorative quarter ticks drawn inside the gauge, so a glance can read the level off the bar. */
const QUARTER_MARKERS = [25, 50, 75] as const;

const edgeButtonVariants = cva(
  'flex h-[calc(var(--eag-space-step,0.25)*6rem)] w-[calc(var(--eag-space-step,0.25)*6rem)] flex-none cursor-pointer select-none items-center justify-center rounded-[var(--radius-circular)] border-none bg-[var(--color-palettes-neutral-20-100)] p-0 [font-family:var(--font-family-body)] !text-[length:var(--text-size-x-small)] leading-[var(--text-leading-x-small)] hover:bg-[var(--color-palettes-neutral-30-100)] active:bg-[var(--color-palettes-neutral-40-100)] focus-visible:[outline:var(--outline-default)_var(--color-palettes-blue-40-100)] focus-visible:[outline-offset:calc(-1*var(--outline-offset))]',
  {
    variants: {
      isEmptyEdge: {
        true: 'text-[var(--color-palettes-red-80-100)]',
        false: 'text-[var(--color-palettes-neutral-500-100)]',
      },
    },
  }
);
const quarterMarkerVariants = cva(
  'pointer-events-none absolute bottom-0 h-[calc(var(--eag-space-step,0.25)*1rem)] w-px -translate-x-1/2 bg-[var(--color-palettes-neutral-70-100)]'
);
const pillVariants = cva(
  'relative box-border h-[calc(var(--eag-space-step,0.25)*4rem)] w-[calc(var(--eag-space-step,0.25)*35rem)] flex-none cursor-pointer select-none overflow-hidden rounded-[var(--radius-medium)] border [touch-action:none] focus-visible:[outline:var(--outline-default)_var(--color-palettes-blue-40-100)] focus-visible:[outline-offset:3px]',
  {
    variants: {
      isRecorded: {
        true: 'border-solid border-[var(--color-palettes-neutral-40-100)] bg-[var(--color-palettes-neutral-30-100)]',
        false:
          'border-dashed border-[var(--color-palettes-neutral-70-100)] bg-[var(--color-palettes-white-10-100)]',
      },
    },
  }
);

export function FuelLevel(props: FuelLevelProps) {
  const min = props.min ?? DEFAULT_LEVEL_MIN;
  const max = props.max ?? DEFAULT_LEVEL_MAX;
  const step = props.step ?? (max - min) * FUEL_LEVEL_DEFAULT_STEP_FRACTION;
  const levelInputProps = {...props, step};
  const level = useLevelInput(levelInputProps);

  /**
   * The precise input is controlled off `props.value`, so snapping here would rewrite the field
   * between keystrokes — with `step={5}`, typing "37" would land on "5" after the first digit.
   * The typed reading is passed through untouched and only snapped once the field is left.
   */
  const handleValueInputChange = (value: number | null) => {
    props.onChange?.(value);
  };

  const handleValueInputBlur: FocusEventHandler<HTMLDivElement> = (event) => {
    const value = props.value ?? null;
    const snapped = snapLevelToProps(value, levelInputProps);

    if (snapped !== value) {
      props.onChange?.(snapped);
    }

    props.onBlur?.(event);
  };

  return (
    <VStack>
      <Label
        isRequired={props.isRequired}
        tooltip={props.tooltip}
        data-testid={suffixTestId('label', props)}
      >
        {props.label}
      </Label>
      <LevelInputRow
        icon="specification/fuel_pump"
        isIconNotFilled
        isDisabled={props.isDisabled}
        gap={ROW_GAP}
        leading={
          <Show when={props.isValueInputVisible}>
            <Box width={VALUE_INPUT_WIDTH} flexShrink={0}>
              <NumberInput
                value={props.value ?? null}
                onChange={handleValueInputChange}
                decimalPlaces={VALUE_INPUT_DECIMAL_PLACES}
                minStepperValue={min}
                maxStepperValue={max}
                step={step}
                suffix={VALUE_INPUT_SUFFIX}
                isDisabled={props.isDisabled}
                isInvalid={props.isInvalid}
                onBlur={handleValueInputBlur}
                aria-label={props.label ?? undefined}
                data-testid={suffixTestId('value', props)}
              />
            </Box>
          </Show>
        }
        data-testid={suffixTestId('fuelLevel', props)}
      >
        <button
          type="button"
          className={cn(edgeButtonVariants({isEmptyEdge: true}))}
          onClick={level.setEmpty}
          disabled={props.isDisabled}
          data-testid={suffixTestId('empty', props)}
        >
          {EMPTY_LABEL}
        </button>
        <div
          className={cn(pillVariants({isRecorded: level.isRecorded}))}
          data-testid={suffixTestId('track', props)}
          {...level.trackProps}
        >
          <Show when={level.isRecorded}>
            <LevelFill
              $fraction={level.fraction ?? 0}
              $bandColor={level.bandColor}
              $radius="medium"
            />
          </Show>
          <Show when={level.hoverFraction}>
            <LevelPreview $fraction={level.hoverFraction ?? 0} />
          </Show>
          {QUARTER_MARKERS.map((marker) => (
            <span
              key={marker}
              className={cn(quarterMarkerVariants())}
              style={{left: `${marker}%`}}
              aria-hidden
              data-testid={suffixTestId(`marker-${marker}`, props)}
            />
          ))}
        </div>
        <button
          type="button"
          className={cn(edgeButtonVariants({isEmptyEdge: false}))}
          onClick={level.setFull}
          disabled={props.isDisabled}
          data-testid={suffixTestId('full', props)}
        >
          {FULL_LABEL}
        </button>
      </LevelInputRow>
      <Show when={props.errorMessage ?? props.helperText}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}
